/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Client-side evaluation of a Builder entry's targeting rules, mirroring the semantics the
 * content API applies server-side (`@builder.io/sdk-react` 5.2.9,
 * `lib/node/blocks-exports.mjs` `filterWithCustomTargeting`).
 *
 * Needed because `_app`'s `shouldRevalidate` returns `false`: the loader runs once per
 * document load, so a server-targeted entry would freeze on the landing URL for the whole
 * client-side session. Fetch every entry untargeted, then pick here on each navigation.
 */

import type { BuilderContent } from '@builder.io/sdk-react';

/** One rule of an entry's `query` array, as the content API returns it. */
export type TargetingRule = {
    property?: string;
    operator?: string;
    value?: unknown;
};

/** The API returns both of these; the SDK's `BuilderContent` declares neither. */
export type TargetedBuilderContent = BuilderContent & {
    query?: TargetingRule[];
    createdDate?: number;
};

export type TargetingAttributes = Record<string, unknown>;

/** Builder strips a trailing slash from the rule value only; we normalise the live path too, so
 * `/cart/` and `/cart` target alike. */
export function normaliseUrlPath(pathname: string): string {
    return pathname !== '/' && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

function compare(operator: string, actual: unknown, expected: unknown): boolean {
    switch (operator) {
        case 'is':
            return actual === expected;
        case 'isNot':
            return actual !== expected;
        case 'contains':
            return String(actual ?? '').includes(String(expected));
        case 'startsWith':
            return String(actual ?? '').startsWith(String(expected));
        case 'endsWith':
            return String(actual ?? '').endsWith(String(expected));
        case 'greaterThan':
            return Number(actual) > Number(expected);
        case 'lessThan':
            return Number(actual) < Number(expected);
        case 'greaterThanOrEqualTo':
            return Number(actual) >= Number(expected);
        case 'lessThanOrEqualTo':
            return Number(actual) <= Number(expected);
        default:
            return false;
    }
}

type CompleteRule = TargetingRule & { property: string; operator: string };

/** "+ Add Target" leaves a `{ property: null, value: null }` row behind; Builder ignores those. */
function isComplete(rule: TargetingRule): rule is CompleteRule {
    return Boolean(rule.property && rule.operator);
}

function matchesRule(rule: CompleteRule, attributes: TargetingAttributes): boolean {
    const { property, operator, value } = rule;

    // A rule on an attribute we do not supply cannot be satisfied. Builder's own filter would
    // let `isNot` pass here; we stay hidden instead, so adding e.g. a Device rule in the editor
    // suppresses the entry rather than silently showing it everywhere.
    if (!(property in attributes)) return false;

    const actual = attributes[property];
    // Per value, not once: the SDK recurses per array element, so the strip reaches each of them.
    const expected = (v: unknown) => (property === 'urlPath' && typeof v === 'string' ? normaliseUrlPath(v) : v);

    // An array value ORs — except under `isNot`, where every element must hold.
    if (Array.isArray(value)) {
        return operator === 'isNot'
            ? value.every((item) => compare(operator, actual, expected(item)))
            : value.some((item) => compare(operator, actual, expected(item)));
    }

    return compare(operator, actual, expected(value));
}

/** One rule against these attributes; an incomplete row ("+ Add Target" left empty) never matches. */
export function matchesTargetingRule(rule: TargetingRule, attributes: TargetingAttributes): boolean {
    return isComplete(rule) && matchesRule(rule, attributes);
}

/**
 * True when every rule on the entry holds and it is within its scheduled window. An entry with
 * no rules matches everywhere, so a placement that must default to hidden needs its entries
 * targeted — see docs/README-BUILDER-NEWSLETTER.md.
 */
export function matchesTargeting(
    entry: TargetedBuilderContent,
    attributes: TargetingAttributes,
    now: Date = new Date()
): boolean {
    // Truthiness and `new Date()` exactly as the SDK does it: a null off the wire must not read as
    // "ended in 1970", and a date that arrives as an ISO string still has to compare.
    const { startDate, endDate } = entry;
    if (startDate && new Date(startDate) > now) return false;
    if (endDate && new Date(endDate) < now) return false;

    return (entry.query ?? []).filter(isComplete).every((rule) => matchesRule(rule, attributes));
}

/**
 * True when the entry carries at least one rule that actually constrains anything.
 *
 * An entry with no effective rules matches every URL — correct SDK semantics, and wrong for a
 * placement that must default to hidden. Every `global` entry in this space was authored with
 * `query: []`, so that is the shape a merchandiser will reach for first.
 */
export function hasEffectiveTargeting(entry: TargetedBuilderContent): boolean {
    return (entry.query ?? []).filter(isComplete).some(({ value }) =>
        // An empty list constrains nothing, and under `isNot` it matches every URL, since
        // `[].every(...)` holds vacuously — the one shape that would slip the default-hidden guard.
        Array.isArray(value) ? value.length > 0 : value !== '' && value != null
    );
}

/**
 * The matching entry for these attributes, oldest first so a later duplicate cannot hijack a
 * live one — the tie-break `resolveBuilderEntry` callers spell as `sort: { createdDate: 1 }`.
 */
export function selectTargetedEntry(
    entries: readonly TargetedBuilderContent[],
    attributes: TargetingAttributes,
    now: Date = new Date()
): TargetedBuilderContent | undefined {
    return [...entries]
        .sort((a, b) => (a.createdDate ?? 0) - (b.createdDate ?? 0))
        .find((entry) => matchesTargeting(entry, attributes, now));
}
